import useSWRMutation from "swr/mutation";
import { axiosInstance } from "../axiosConfig";
import { handleError } from "../handleError";

const TEST_URL = "/tests";
const TEST_START_KEY = "/start";
const TEST_NEXT_QUESTION_KEY = "next-question";
const TEST_ANSWER_KEY = "answer";
const TEST_COMPLETE_KEY = "/complete";
const TEST_GENERATE_KEY = "/generate";
const TEST_BUY_KEY = "/buy";

interface StartTestBody {
  isn: string;
  sessionId: string;
  dateTime: string;
}

interface NextQuestionBody {
  isn: string;
  sessionId: string;
}

interface CompleteTestBody {
  isn: string;
  sessionId: string;
}

interface NextQuestionResponse {
  data: {
    testFlowQuestionId: string;
    question: string;
    flip: boolean;
    answeredQuestionsCount: number;
    notAnsweredQuestionsCount: number;
    possibleAnswers: Array<{ value: string; order: string }>;
  };
  error: {
    code: string;
    message: string;
  };
}

interface CompleteTestResponse {
  data: {
    isSuccess: boolean;
    isFailure: boolean;
    error: {
      code: string;
      message: string;
    };
  };
}

interface GenerateTestResponse {
  data: {
    value: string;
    isSuccess: boolean;
    isFailure: boolean;
    error: {
      code: string;
      message: string;
    };
  };
  error: {
    code: string;
    message: string;
  };
}

interface BuyTestResponse {
  data: {
    value: string;
    isSuccess: boolean;
    isFailure: boolean;
    error: {
      code: string;
      message: string;
    };
  };
  error: {
    code: string;
    message: string;
  };
}

const useTestMutation = () => {
  const startTestFetcher = (url: string, { arg }: { arg: StartTestBody }) => {
    return axiosInstance
      .post(`${TEST_URL}${TEST_START_KEY}`, arg)
      .then(handleError);
  };

  const nextQuestionFetcher = (
    url: string,
    { arg }: { arg: NextQuestionBody }
  ) => {
    return axiosInstance.get<NextQuestionResponse>(
      `${TEST_URL}/codes/${arg.isn}/sessions/${arg.sessionId}/next-question`
    );
  };

  const answerQuestionFetcher = (
    url: string,
    {
      arg,
    }: {
      arg: {
        isn: string;
        sessionId: string;
        questionId: string;
        score: number;
      };
    }
  ) => {
    const { isn, sessionId, questionId, score } = arg;
    return axiosInstance
      .patch(
        `${TEST_URL}/codes/${isn}/sessions/${sessionId}/questions/${questionId}/answer`,
        { score }
      )
      .then(handleError);
  };

  const completeTestFetcher = (
    url: string,
    { arg }: { arg: CompleteTestBody }
  ) => {
    const { isn, sessionId } = arg;
    return axiosInstance
      .patch<CompleteTestResponse>(
        `${TEST_URL}/codes/${isn}/sessions/${sessionId}${TEST_COMPLETE_KEY}`
      )
      .then(handleError);
  };

  const generateTestFetcher = (
    url: string,
    { arg }: { arg: { testName: string; userName: string } }
  ) => {
    const { testName, userName } = arg;
    return axiosInstance
      .post<GenerateTestResponse>(
        `${TEST_URL}${TEST_GENERATE_KEY}/${testName}/user/${userName}`
      )
      .then(handleError);
  };

  const buyTestFetcher = (url: string, { arg }: { arg: { isn: string } }) => {
    return axiosInstance
      .post<BuyTestResponse>(`${TEST_URL}${TEST_BUY_KEY}/${arg.isn}`)
      .then(handleError);
  };

  const getResultPdfFetcher = (url: string, { arg }: { arg: string }) => {
    return axiosInstance
      .get(`${TEST_URL}/result/pdf/${arg}`, { responseType: "blob" })
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", `test-result-${arg}.pdf`);
        document.body.appendChild(link);
        link.click();
        link.remove();
      })
      .catch(handleError);
  };

  const printPdfFetcher = (url: string, { arg }: { arg: string }) => {
    return axiosInstance
      .get(`${TEST_URL}/result/pdf/${arg}`, { responseType: "blob" })
      .then((response) => {
        const blob = new Blob([response.data], { type: "application/pdf" });
        const url = window.URL.createObjectURL(blob);
        const iframe = document.createElement("iframe");
        iframe.style.display = "none";
        iframe.src = url;
        document.body.appendChild(iframe);
        iframe.onload = function () {
          iframe.contentWindow?.print();
        };
      })
      .catch(handleError);
  };

  const printResultPdf = useSWRMutation(
    `${TEST_URL}-print-pdf`,
    printPdfFetcher
  );

  const startTest = useSWRMutation(
    `${TEST_URL}-${TEST_START_KEY}`,
    startTestFetcher
  );

  const nextQuestion = useSWRMutation(
    `${TEST_URL}-${TEST_NEXT_QUESTION_KEY}`,
    nextQuestionFetcher
  );

  const answerQuestion = useSWRMutation(
    `${TEST_URL}-${TEST_ANSWER_KEY}`,
    answerQuestionFetcher
  );

  const completeTest = useSWRMutation(
    `${TEST_URL}-${TEST_COMPLETE_KEY}`,
    completeTestFetcher
  );

  const generateTest = useSWRMutation(
    `${TEST_URL}-${TEST_GENERATE_KEY}`,
    generateTestFetcher
  );

  const buyTest = useSWRMutation(`${TEST_URL}-${TEST_BUY_KEY}`, buyTestFetcher);

  const resultPdf = useSWRMutation(
    `${TEST_URL}-result-pdf`,
    getResultPdfFetcher
  );

  const getStaticPdfFetcher = async (url: string) => {
    const response = await axiosInstance.get(url, { responseType: "blob" });
    return response.data;
  };

  const getStaticPdf = useSWRMutation(
    "/main/static-pdf",
    getStaticPdfFetcher
  );
  return {
    startTest,
    nextQuestion,
    answerQuestion,
    completeTest,
    generateTest,
    buyTest,
    resultPdf,
    printResultPdf,
    getStaticPdf
  };
};

export default useTestMutation;
